import { useCallback, useEffect, useState } from 'react'
import { ApiClientError } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { ProcessLogRow, SatelitLogItem } from '../../api/adminTypes'
import { formatDateTime } from '../trading/tradingConstants'
import { AdminNotice, AdminTabs, EmailLookupForm } from './adminShared'
import { describeAdminError, redactedJson, useEmailParam } from './adminHelpers'
function todayYyyymmdd(): string {
  const now = new Date()
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
}

/** `value` arrives as a JSON STRING (see contract #27/#28), unlike the
 * Satelit log's `params`/`response` (already-parsed objects) — parse first
 * so `redactSecrets` can actually walk the keys; an unparseable value has no
 * keys to redact, so it is shown as-is (same as the backend's own
 * `valuePretty: "null"` fallback for a non-JSON value). */
function redactedProcessValue(raw: string | undefined): string {
  if (raw === undefined) return ''
  try {
    return redactedJson(JSON.parse(raw))
  } catch {
    return raw
  }
}

/**
 * Equivalent of legacy `AdminUserLogComponent` (route `admin/log/user`) —
 * per-user process/event log by date, per the audit. Two contract
 * endpoints, both scoped to one user:
 *  - #10 `/admin/log/satelitApi` — cursor-paged Satelit upstream API log;
 *  - #27/#28 `/v3/admin/bot/log/processes/{userId}/{date}[/{processId}]` —
 *    spotbot process log. **Answers 503 `process_log_not_available` until
 *    spotbot ships the view** — shown as "not available yet", never as an
 *    empty list (an empty list would read as "no logs for this user").
 * `date` always sent explicitly (`todayYyyymmdd`) to avoid the legacy
 * "empty date means today WITH dashes, matches nothing" quirk.
 */
export function AdminUserLogPage() {
  const [email, setEmail] = useEmailParam()
  const [tab, setTab] = useState<'satelit' | 'process'>('satelit')

  const [satelitItems, setSatelitItems] = useState<SatelitLogItem[]>([])
  const [satelitCursor, setSatelitCursor] = useState<string | null>(null)
  const [satelitFinished, setSatelitFinished] = useState(false)
  const [satelitFeedback, setSatelitFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()

  const [userId, setUserId] = useState('')
  const [date, setDate] = useState(todayYyyymmdd())
  const [processId, setProcessId] = useState('')
  const [processRows, setProcessRows] = useState<ProcessLogRow[] | null>(null)
  const [processUnavailable, setProcessUnavailable] = useState(false)
  const [processFeedback, setProcessFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()

  const loadSatelit = useCallback((targetEmail: string, cursor?: string) => {
    setSatelitFeedback(undefined)
    adminClient
      .satelitLogs(targetEmail, cursor)
      .then((page) => {
        setSatelitItems((prev) => (cursor ? [...prev, ...page.items] : page.items))
        setSatelitCursor(page.cursor)
        setSatelitFinished(!!page.finished)
      })
      .catch((err) => setSatelitFeedback(describeAdminError(err)))
  }, [])

  function loadProcess() {
    setProcessRows(null)
    setProcessUnavailable(false)
    setProcessFeedback(undefined)
    adminClient
      .processLogs(userId, date, processId || undefined)
      .then((rows) => setProcessRows(rows))
      .catch((err: unknown) => {
        if (err instanceof ApiClientError && err.status === 503) {
          setProcessUnavailable(true)
          return
        }
        setProcessFeedback(describeAdminError(err))
      })
  }

  function onEmailSubmit(value: string) {
    setEmail(value)
    setSatelitItems([])
    setSatelitCursor(null)
    setSatelitFinished(false)
    if (value) loadSatelit(value)
  }

  // Loads on mount too — e.g. arriving via a cross-link that already
  // carries `?email=` (`AdminUsersPage`'s "Logs" link), not just via the
  // in-page form submit.
  useEffect(() => {
    if (email) loadSatelit(email)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — user logs</h1>
      </div>
      <div className="portal-card">
        <AdminTabs
          tabs={[
            { id: 'satelit', label: 'Satelit API log' },
            { id: 'process', label: 'Process log' },
          ]}
          active={tab}
          onChange={setTab}
        />

        {tab === 'satelit' && (
          <div>
            <EmailLookupForm email={email} onSubmit={onEmailSubmit} />
            <AdminNotice feedback={satelitFeedback} />
            {!email && <p className="portal-text-muted">Enter a user e-mail to view their Satelit API log.</p>}
            {email && satelitItems.length === 0 && !satelitFeedback && <p>Loading…</p>}
            {satelitItems.map((item, i) => (
              <div className="portal-card" key={`${item.createdAt}-${i}`}>
                <dl className="portal-kv">
                  <dt>Date</dt>
                  <dd>{formatDateTime(item.createdAt)}</dd>
                  <dt>Type</dt>
                  <dd>{item.type}</dd>
                  <dt>Result</dt>
                  <dd>{item.result}</dd>
                </dl>
                <details>
                  <summary>Params</summary>
                  <pre>{redactedJson(item.params)}</pre>
                </details>
                <details>
                  <summary>Response</summary>
                  <pre>{redactedJson(item.response)}</pre>
                </details>
              </div>
            ))}
            {!satelitFinished && satelitCursor && email && (
              <button type="button" onClick={() => loadSatelit(email, satelitCursor)}>
                Load more
              </button>
            )}
          </div>
        )}

        {tab === 'process' && (
          <div>
            <form
              className="portal-form"
              onSubmit={(e) => {
                e.preventDefault()
                loadProcess()
              }}
            >
              <label htmlFor="process-userid">User id (serviceId)</label>
              <input id="process-userid" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="5001" />
              <label htmlFor="process-date">Date (YYYYMMDD)</label>
              <input id="process-date" value={date} onChange={(e) => setDate(e.target.value)} />
              <label htmlFor="process-id">Process id (optional)</label>
              <input id="process-id" value={processId} onChange={(e) => setProcessId(e.target.value)} />
              <button type="submit" disabled={!userId || !date}>
                Search
              </button>
            </form>
            <AdminNotice feedback={processFeedback} />
            {processUnavailable && (
              <p className="portal-notice portal-notice--warning">Process logs are not available yet for this backend.</p>
            )}
            {processRows !== null && processRows.length === 0 && !processUnavailable && (
              <p className="portal-text-muted">No process log rows for this user/date.</p>
            )}
            {processRows !== null &&
              processRows.map((row, i) => (
                <div className="portal-card" key={`${row.processId}-${row.logIndex}-${i}`}>
                  <dl className="portal-kv">
                    <dt>Date</dt>
                    <dd>{formatDateTime(row.date)}</dd>
                    <dt>Level</dt>
                    <dd>{row.level}</dd>
                    <dt>Process id</dt>
                    <dd>{row.processId}</dd>
                  </dl>
                  {row.value !== undefined && (
                    <details>
                      <summary>Value</summary>
                      <pre>{redactedProcessValue(row.value)}</pre>
                    </details>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}
